// PUBLIC per-MESSAGE Global Impact page.
// - Accessible without login (no requireAuth, no /me call).
// - Reuses react-globe.gl (same engine as personal Impact) with a
//   distinct copy palette: THIS MESSAGE HAS TRAVELLED / whole-message metrics.
// - Never opens JourneyCardModal, never renders SHARE/SAVE/COPY-CARD controls.
// - Never calls POST /messages/{id}/share — no referral token is generated
//   by opening this page.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { api, formatNumber } from "@/lib/api";
import { useSeoTags } from "@/lib/seo";

const GEO_URL = "/countries.geojson";

export default function GlobalImpactPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const globeRef = useRef();
  const [data, setData] = useState(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [hoverPlace, setHoverPlace] = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [ringPlaces, setRingPlaces] = useState([]);
  const [countries, setCountries] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [topOffset, setTopOffset] = useState(0);

  const globeMaterial = useMemo(
    () => new THREE.MeshPhongMaterial({ color: "#F2F1EC", transparent: false, opacity: 1 }),
    []
  );

  useEffect(() => {
    setReducedMotion(!!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
    // Measure the real rendered VOIHU header height so the Global Impact
    // content ALWAYS starts below it — never behind its backdrop-blur.
    const measure = () => {
      const header = document.querySelector('[data-testid="nav-header"]');
      const h = header ? header.getBoundingClientRect().height : 0;
      setTopOffset(h);
      const isMobile = window.innerWidth < 640;
      const reserved = isMobile ? 260 : 200;
      setSize({
        w: window.innerWidth,
        h: Math.max(320, window.innerHeight - reserved - h),
      });
    };
    measure();
    window.addEventListener("resize", measure);
    // Nav can re-render (auth state / notifications), so re-measure after
    // one paint tick — cheap, no observer needed.
    const t = setTimeout(measure, 200);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);

  useEffect(() => {
    api.get(`/messages/${id}/global-impact`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      });
    fetch(GEO_URL).then((r) => r.json())
      .then((j) => setCountries(j?.features || []))
      .catch(() => setCountries([]));
  }, [id]);

  useEffect(() => {
    return () => {
      try {
        const g = globeRef.current;
        if (g && typeof g.renderer === "function") {
          const renderer = g.renderer();
          renderer?.dispose?.();
          try { renderer?.forceContextLoss?.(); } catch (_) {}
        }
      } catch (_) {}
      try { globeMaterial.dispose(); } catch (_) {}
    };
  }, [globeMaterial]);

  useEffect(() => {
    if (!globeRef.current || !data) return;
    const g = globeRef.current;
    g.controls().autoRotate = false;
    g.controls().enableZoom = true;
    const o = data.origin;
    if (o?.lat != null && o?.lon != null) {
      g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 3.6 }, 0);
      if (reducedMotion) {
        g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 2.4 }, 0);
      } else {
        const t1 = setTimeout(() => {
          g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 1.9 }, 2000);
        }, 250);
        const t2 = setTimeout(() => {
          g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 2.4 }, 1200);
          g.controls().autoRotate = true;
          g.controls().autoRotateSpeed = 0.28;
        }, 2500);
        return () => { clearTimeout(t1); clearTimeout(t2); };
      }
    }
  }, [data, reducedMotion]);

  useEffect(() => {
    const places = data?.message?.places || [];
    if (!places.length) { setRingPlaces([]); return; }
    setRingPlaces([]);
    const start = reducedMotion ? 0 : 2600;
    const timers = places.map((p, i) =>
      setTimeout(() => {
        setRingPlaces((prev) => [
          ...prev,
          { lat: p.lat, lng: p.lon, city: p.city, country: p.country,
            reached: p.reached, id: `${p.city}-${i}` },
        ]);
      }, start + (reducedMotion ? 0 : i * 300))
    );
    return () => timers.forEach(clearTimeout);
  }, [data, reducedMotion]);

  // SEO — public page; per-MESSAGE canonical.
  useSeoTags(
    {
      title: data?.title ? `The journey of "${data.title}" — VOIHU` : "The journey of this MESSAGE — VOIHU",
      description:
        "Watch how this MESSAGE has travelled across the world on VOIHU — voices, countries, and human generations reached so far.",
      canonical: `https://voihuworld.com/message/${id}/global-impact`,
      ogType: "article",
    },
    [id, data?.title],
  );

  // ── PER-HOP GEOMETRY (kept BEFORE early returns — hook rules) ───────
  // The MESSAGE's travel is expressed as a moving BLUE TAIL along each
  // parent→child arc. Two independent visual dimensions are computed
  // per hop:
  //   * altitude  → mostly flat curvature, ONLY lifted for near-antipodal
  //                 pairs to keep them stable in react-globe.gl. Altitude
  //                 is NEVER used as the distance indicator.
  //   * dashLength → the visible travelling segment. Short hops get a
  //                 short elegant tail, long hops get a proportionally
  //                 longer tail — this is where distance is felt.
  const origin = data?.origin || null;
  const msg = data?.message || null;
  const originPoint = (origin?.lat != null && origin?.lon != null)
    ? [{ lat: origin.lat, lng: origin.lon, id: "origin",
         city: origin.city || origin.country, country: origin.country, isOrigin: true }]
    : [];

  const arcs = useMemo(() => {
    if (!msg) return [];
    const _toRad = (d) => (d * Math.PI) / 180;
    const _ang = (aLat, aLon, bLat, bLon) => {
      const p1 = _toRad(aLat), p2 = _toRad(bLat);
      const dp = _toRad(bLat - aLat), dl = _toRad(bLon - aLon);
      const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
      return 2 * Math.asin(Math.min(1, Math.sqrt(a))); // 0..π
    };
    // Flat curvature for short/medium hops (elegant, minimal). Only
    // near-antipodal pairs get a lift — needed to prevent react-globe.gl
    // arc meshes from collapsing when the two great circles between the
    // endpoints become ambiguous.
    const _alt = (aLat, aLon, bLat, bLon) => {
      const t = _ang(aLat, aLon, bLat, bLon) / Math.PI;
      const antipodalBoost = t > 0.5 ? Math.pow((t - 0.5) / 0.5, 1.8) * 0.50 : 0;
      return 0.20 + antipodalBoost;
    };
    // Distance → visible-tail length. Non-linear so short hops stay
    // short and elegant while intercontinental hops feel truly far.
    // Saturates around half the sphere so the very longest routes still
    // have an animated gap (never a solid line).
    const _dash = (aLat, aLon, bLat, bLon) => {
      const t = Math.min(1, (_ang(aLat, aLon, bLat, bLon) / Math.PI) * 2);
      return 0.15 + Math.pow(t, 0.65) * 0.70; // 0.15..0.85
    };

    // Prefer REAL parent → child ancestry from the backend.
    const list = Array.isArray(msg.routes) ? msg.routes : null;
    if (list && list.length > 0) {
      return list.map((r) => ({
        id: `${r.from.country}-${r.from.city || ""}->${r.to.country}-${r.to.city || ""}`,
        startLat: r.from.lat, startLng: r.from.lon,
        endLat: r.to.lat,   endLng: r.to.lon,
        altitude:   _alt(r.from.lat, r.from.lon, r.to.lat, r.to.lon),
        dashLength: _dash(r.from.lat, r.from.lon, r.to.lat, r.to.lon),
        color: ["rgba(31,79,161,0.95)", "rgba(31,79,161,0.10)"],
        label: r.to.city
          ? `${r.to.city.toUpperCase()} · ${(r.to.country || "").toUpperCase()}`
          : (r.to.country || "").toUpperCase(),
      }));
    }
    // Legacy fallback — only used when the backend didn't send `routes`.
    if (!origin) return [];
    return (msg.places || []).map((p) => ({
      id: `origin->${p.country}-${p.city || ""}`,
      startLat: origin.lat, startLng: origin.lon,
      endLat: p.lat, endLng: p.lon,
      altitude:   _alt(origin.lat, origin.lon, p.lat, p.lon),
      dashLength: _dash(origin.lat, origin.lon, p.lat, p.lon),
      color: ["rgba(31,79,161,0.95)", "rgba(31,79,161,0.10)"],
      label: p.city
        ? `${p.city.toUpperCase()} · ${(p.country || "").toUpperCase()}`
        : (p.country || "").toUpperCase(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg?.routes, msg?.places, origin?.lat, origin?.lon]);

  const points = useMemo(() => {
    if (!msg) return [];
    return [
      ...originPoint,
      ...(msg.places || []).map((p) => ({
        lat: p.lat, lng: p.lon, id: `${p.city || "country"}-${p.country}`,
        city: p.city, country: p.country, reached: p.reached, isOrigin: false,
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg?.places, origin?.lat, origin?.lon]);

  // ── ZOOM-AWARE VISUAL SCALING ─────────────────────────────────────
  // Camera altitude is tracked so markers, strokes and labels can
  // scale DOWN as the viewer approaches the Earth (world → continent
  // → country → city). react-globe.gl reports altitude via `onZoom`
  // in globe-radii units; ~2.4 is our world view, ~0.4 is close.
  // Updates are quantised (0.15 step) so a slow drag doesn't produce
  // hundreds of re-renders — the useMemo'd `arcs` reference stays
  // stable, so the dash animation never restarts.
  const [zoomAlt, setZoomAlt] = useState(2.4);
  const lastZoomRef = useRef(2.4);
  const handleZoom = (pov) => {
    const a = pov?.altitude;
    if (typeof a !== "number") return;
    if (Math.abs(a - lastZoomRef.current) < 0.15) return;
    lastZoomRef.current = a;
    setZoomAlt(a);
  };
  // 0..1 where 1 = world view, 0 = as close as we allow. Clamped so
  // markers never collapse to zero and never overflow either bound.
  const _zoomScale = Math.max(0.18, Math.min(1, zoomAlt / 2.4));

  // Only the ORIGIN keeps a persistent label. Every VOICE label is
  // dropped from the always-on layer — top-N labelling collided badly
  // on regions with dense clusters (US east coast, EU). Cities are
  // still available on hover via `hoverPlace`. The perceived
  // "US-only labels" bug was actually the top-6 heuristic biting on
  // country-clusters, not a US-specific code path.
  const labelsData = useMemo(() => {
    return originPoint;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin?.lat, origin?.lon, origin?.city, origin?.country]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-8 text-center" data-testid="global-impact-notfound">
        <div className="oh-label">Not available</div>
        <div className="font-serif-editorial text-3xl mt-4 max-w-md leading-tight">
          This MESSAGE is not publicly visible yet.
        </div>
        <button onClick={() => navigate(`/message/${id}`)}
                className="btn-ghost mt-8" data-testid="global-impact-back-message">
          Back to MESSAGE
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center oh-label" data-testid="global-impact-loading">
        Loading the journey…
      </div>
    );
  }

  const empty = !(msg.has_travelled);

  return (
    <div className="fixed inset-x-0 bottom-0 bg-[color:var(--oh-bg)]"
         style={{ top: topOffset }}
         onMouseMove={(e) => {
           // Only update mouse state while a point is actually hovered —
           // otherwise every cursor movement re-renders the tree and
           // react-globe.gl rebuilds the arc meshes (dash animation
           // restarts). This is the primary cause of the freeze / restart
           // during globe drag/rotation/hover reported in production.
           if (hoverPlace) setMouse({ x: e.clientX, y: e.clientY });
         }}
         data-testid="global-impact-page">
      <div className="absolute top-0 left-0 right-0"
           style={{ height: size.h, pointerEvents: "auto" }}>
        <Globe
          ref={globeRef}
          width={size.w}
          height={size.h}
          backgroundColor="rgba(0,0,0,0)"
          globeImageUrl={null}
          showGlobe={true}
          showAtmosphere={false}
          globeMaterial={globeMaterial}
          polygonsData={countries}
          polygonAltitude={0.005}
          polygonCapColor={() => "rgba(17,17,17,0.05)"}
          polygonSideColor={() => "rgba(17,17,17,0.02)"}
          polygonStrokeColor={() => "rgba(17,17,17,0.35)"}
          arcsData={arcs}
          arcColor={"color"}
          // Route stroke tapers slightly as the camera approaches Earth
          // so a Munich→Oviedo arc doesn't look absurdly thick at city
          // zoom, but stays confidently readable at world view. Bounded.
          arcStroke={Math.max(0.5, 1.2 * (0.6 + 0.4 * _zoomScale))}
          arcAltitude={"altitude"}
          arcAltitudeAutoScale={0}
          arcDashLength={"dashLength"}
          arcDashGap={1.4}
          arcDashAnimateTime={reducedMotion ? 0 : 2400}
          onZoom={handleZoom}
          ringsData={ringPlaces}
          ringColor={() => (t) => `rgba(217,45,32,${1 - t})`}
          ringMaxRadius={2.2 * _zoomScale}
          ringPropagationSpeed={2}
          ringRepeatPeriod={reducedMotion ? 99999 : 1400}
          pointsData={points}
          pointLat={"lat"} pointLng={"lng"}
          pointColor={(d) => d.isOrigin ? "#111111" : "#D92D20"}
          // Marker size scales DOWN with zoom so red VOICES don't blot
          // out cities when the camera is close. Origin stays a touch
          // larger than a VOICE at every zoom level to remain visually
          // distinguishable (~1.6× a VOICE at all altitudes).
          pointAltitude={(d) => (d.isOrigin ? 0.02 : 0.008) * _zoomScale}
          pointRadius={(d) => (d.isOrigin ? 1.1 : 0.6) * _zoomScale}
          onPointHover={setHoverPlace}
          onPointClick={setHoverPlace}
          labelsData={labelsData}
          labelLat={"lat"} labelLng={"lng"}
          labelText={(d) => d.isOrigin ? "THIS MESSAGE STARTED HERE" : ""}
          // Origin label also tapers so at city-zoom it stays proportional.
          labelSize={(d) => (d.isOrigin ? 1.4 : 0.95) * _zoomScale}
          labelDotRadius={0}
          labelColor={(d) => d.isOrigin ? "#111111" : "rgba(17,17,17,0.72)"}
          labelResolution={2}
          labelAltitude={(d) => (d.isOrigin ? 0.06 : 0.012) * _zoomScale}
        />
      </div>

      <button onClick={() => navigate(`/message/${id}`)}
              className="absolute top-6 left-6 oh-label z-10 hover:text-[color:var(--oh-red)]"
              data-testid="global-impact-back">← Back to MESSAGE</button>

      <div className="absolute top-6 right-6 text-right z-10 max-w-[52vw] sm:max-w-md">
        <div className="oh-label">The journey of this MESSAGE</div>
        <div className="font-serif-editorial text-xl sm:text-3xl mt-2 leading-tight" data-testid="global-impact-title">
          {data.title}
        </div>
        {origin?.country && (
          <div className="oh-label mt-3 text-[color:var(--oh-ink-3)]" data-testid="global-impact-origin">
            {origin.city
              ? `Started in ${origin.city}, ${origin.country}`
              : `Started in ${origin.country}`}
          </div>
        )}
      </div>

      {hoverPlace && (
        <div className="fixed z-20 pointer-events-none bg-[color:var(--oh-bg)] border oh-hairline px-4 py-3 shadow"
             style={{
               left: Math.min(mouse.x + 14, size.w - 260),
               top: Math.min(mouse.y + 14, size.h - 120),
             }}
             data-testid="global-impact-tooltip">
          <div className="font-serif-editorial text-lg leading-tight">
            {hoverPlace.city
              ? `${(hoverPlace.city || "").toUpperCase()}, ${(hoverPlace.country || "").toUpperCase()}`
              : (hoverPlace.country || "").toUpperCase()}
          </div>
          {hoverPlace.isOrigin ? (
            <div className="oh-label mt-2">This MESSAGE started here</div>
          ) : (
            <div className="oh-label mt-2">
              Reached through {formatNumber(hoverPlace.reached)}× on the way
            </div>
          )}
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 z-10 max-h-[52vh] overflow-y-auto border-t oh-hairline bg-[color:var(--oh-bg)]/95 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-6 pb-8">
          <div className="oh-label">This MESSAGE has travelled</div>

          {empty && (
            <div className="mt-4 font-serif-editorial text-xl leading-snug" data-testid="global-impact-empty">
              This MESSAGE has only just begun travelling.
              {origin?.city
                ? ` It started in ${origin.city}, ${origin.country}.`
                : (origin?.country ? ` It started in ${origin.country}.` : "")}
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-6">
            <Stat label="Voices"
                  v={msg.voices}
                  tid="global-impact-voices" />
            <Stat label="Countries"
                  v={msg.countries_count}
                  tid="global-impact-countries" />
            <Stat label="Human generations"
                  v={msg.human_generations}
                  tid="global-impact-generations" />
            <Stat label="Human distance"
                  v={msg.longest_reach_km || 0}
                  suffix={msg.longest_reach_km ? "km" : ""}
                  tid="global-impact-distance" />
          </div>

          {msg.places?.length > 0 && (
            <div className="mt-8 border-t oh-hairline pt-6">
              <div className="oh-label" data-testid="global-impact-journey-title">Countries this MESSAGE has reached</div>
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 font-serif-editorial text-lg leading-snug"
                   data-testid="global-impact-journey">
                {msg.places.slice(0, 20).map((p, i) => (
                  <span key={`${p.city}-${p.country}-${i}`}>
                    {p.country}
                    {i < Math.min(msg.places.length, 20) - 1 && (
                      <span className="text-[color:var(--oh-ink-3)] px-1">→</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, v, tid, suffix }) {
  return (
    <div>
      <div className="oh-label">{label}</div>
      <div className="text-3xl sm:text-5xl font-light mt-2 tabular" data-testid={tid}>
        {(Number(v) || 0).toLocaleString("en-US")}{suffix ? ` ${suffix}` : ""}
      </div>
    </div>
  );
}
